<template>
    <div id="kliniken-header" :class="{ patient: enableActivePatient, 'disable-patient': !enableActivePatient, 'test-bg': isTest }">
        <div id="kliniken-logo" :class="{ minimized: minimized, 'test-bg': isTest }" @click.stop="toggleSystemInfo">
            <h1 aria-hidden="false">Kliniken patientsystem</h1>
            <img v-if="minimized" alt="Kliniken patientsystem" src="@/assets/kliniken-logo-small.svg" />
            <img v-else-if="isTest" src="@/assets/icons/testmiljö.svg">
            <img v-else alt="Kliniken patientsystem" src="@/assets/kliniken-logo.svg" />
            <div
                id="systeminformation-popup"
                v-show="systemInfoOpen"
                class="information-details left systeminformation-popup"
                @click.stop
            >
                <SystemInformation />
            </div>
        </div>
        <section v-if="enableActivePatient" class="patient-section">
            <h2 aria-hidden="false">Toppmeny</h2>
            <div :class="{ 'button-wrapper': true }" id="patient-information">
                <a
                    v-if="personId"
                    id="patient-uppmarksamhet"
                    v-tooltip="{
                        content: 'Uppmärksamhetssignaler',
                        theme: 'kliniken-tooltip',
                    }"
                >
                    <AlertInformationWidget
                        v-if="personId"
                        class="alertSymbol"
                        :patient="patient"
                        :alertInformationSymbol="alertInformationSymbol"
                        stroke-color="#277692"
                    />
                    <div
                        id="uppmarksamhet-popup"
                        class="information-details left uppmarksamhet-details"
                        v-show="uppmarksamhetOpen"
                    >
                        <template v-if="personId">
                            <div class="details-row">
                                <div class="patient-icon">
                                    <img src="../assets/patient_filled.svg" />
                                </div>
                                <div class="details-text">
                                    <div class="small">Aktiv patient</div>
                                    <div class="big">{{ personId_formatted }}</div>
                                    <div class="big">{{ namn }}</div>
                                </div>
                            </div>

                            <Button
                                class="open-alertinformation"
                                size="secondary"
                                type="button"
                                role="button"
                                v-on:click="openAlertInformationTab()"
                            >
                                Visa uppmärksamhetsignaler
                            </Button>
                        </template>
                    </div>
                </a>
                <button
                    v-if="personId"
                    class="patient-details patient-selected"
                    type="button"
                    role="button"
                    v-tooltip="{
                        content: 'Vald patient',
                        theme: 'kliniken-tooltip',
                        disabled: Boolean(personId) === false,
                    }"
                >
                    <div class="id-container">
                        <span class="person-id" @click="openPatientoversikt">{{ personId_formatted }}</span>
                        <span class="id-clip" @click="copyPersonIdToClipboard">
                            <img width="15" height="15" src="@/assets/copy_blue.svg" />
                        </span>
                        <span v-if="avliden" class="person-age">Avliden</span>
                        <span v-else-if="dateOfBirth" class="person-age">{{ `${countYears()} år` }}</span>
                    </div>
                    <div @click="deselectPatient" class="close-x">&times;</div>
                    <span class="patient-name" @click="openPatientoversikt">
                        <span v-if="skyddadIdentitet">Skyddad identitet</span>
                        <span v-else-if="sekretessmarkering">Sekretessmarkering</span>
                        <span v-else>
                            <span
                                v-for="item in patientFirstName"
                                :style="{ 'text-decoration': item.underline ? 'underline' : 'none' }"
                                style="margin-right: 0.5ch"
                                :key="item.fornamn"
                                >{{ item.fornamn }}</span
                            >{{ efternamn }}
                        </span>
                    </span>
                    <img src="@/assets/chevron_down_dark_thick.svg" id="open-patient-popup" />
                </button>
                <button
                    v-else
                    id="open-select-patient-popup"
                    class="patient-details"
                    type="button"
                    role="button"
                    v-tooltip="{
                        content: 'Vald patient',
                        theme: 'kliniken-tooltip',
                        disabled: Boolean(personId) === false,
                    }"
                >
                    <span>Ingen patient vald</span>
                    <img class="patient-arrow" src="../assets/dropdown-arrow-white.svg" />
                </button>
                <div
                    id="patient-popup"
                    class="information-details patient-popup"
                    :class="{ 'has-person-id': !!personId }"
                    v-show="patientDetailsOpen"
                >
                    <template v-if="personId">
                        <div class="details-row">
                            <div class="patient-icon">
                                <img src="../assets/patient_filled.svg" />
                            </div>
                            <div class="details-text">
                                <div class="small">Aktiv patient</div>
                                <div class="big">{{ personId_formatted }}</div>
                                <div class="big">{{ namn }}</div>
                                <div v-if="patient.telefon" class="small">
                                    <span class="bold">Telefon </span>
                                    <span>{{ patient.telefon }}</span>
                                </div>
                                <div v-if="patient.email" class="small">
                                    <span class="bold">E-post </span>
                                    <span>{{ patient.email }}</span>
                                </div>
                                <div class="small id-container">
                                    <span class="bold">PatientID </span>
                                    <span>{{ patient.patientId }}</span>
                                    <span class="id-clip" @click="copyPatientIdToClipboard">
                                        <img src="@/assets/copy_grey.svg" />
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="details-buttons">
                            <Button
                                v-if="purConfigured && patientHasPersonIdTypePersonalNumber"
                                type="button"
                                variant="contour"
                                size="small"
                                style="padding: 0.8rem"
                                v-on:click="
                                    updatePurExternalPatientData({
                                        personId,
                                        action: 'edit',
                                        currentPatientData: patient,
                                    })
                                "
                            >
                                <img src="../assets/download-blue.svg" />Uppdatera personuppgifter
                            </Button>
                            <button
                                class="hollow-button"
                                type="button"
                                role="button"
                                v-on:click="openEditTab(), (patientDetailsOpen = false)"
                            >
                                <img src="../assets/edit.svg" />Redigera patient
                            </button>
                            <button
                                class="hollow-button"
                                type="button"
                                role="button"
                                v-on:click="openSamtyckeTab(), (patientDetailsOpen = false)"
                            >
                                <img src="../assets/samtycker-gray.svg" />Patientsamtycken
                            </button>
                            <div class="pt-spacer" />
                            <button
                                class="hollow-button"
                                type="button"
                                role="button"
                                v-on:click="openSearchTab(), (patientDetailsOpen = false)"
                            >
                                <img class="soft-gray" src="../assets/byt_patient.svg" />Byt patient
                            </button>
                            <button
                                class="hollow-button"
                                type="button"
                                role="button"
                                v-on:click="deselectPatient(), (patientDetailsOpen = false)"
                            >
                                <img class="soft-gray" src="@/assets/avvalj_patient.svg" />Avvälj patient
                            </button>
                            <button
                                class="hollow-button"
                                type="button"
                                role="button"
                                v-on:click="openPatientTab(), (patientDetailsOpen = false)"
                            >
                                <img class="soft-gray" src="../assets/ny_patient.svg" />Skapa patient
                            </button>
                        </div>
                    </template>
                    <template v-else>
                        <span class="warning"> Vissa funktioner i Kliniken kan inte nås utan aktiv patient. </span>
                        <div class="details-buttons">
                            <button
                                type="button"
                                role="button"
                                class="blue-button"
                                v-on:click="openSearchTab(), (patientDetailsOpen = false)"
                            >
                                <img src="../assets/byt_patient.svg" />Välj patient
                            </button>
                            <button
                                type="button"
                                role="button"
                                class="blue-button"
                                v-on:click="openPatientTab(), (patientDetailsOpen = false)"
                            >
                                <img src="../assets/ny_patient.svg" />Skapa patient
                            </button>
                        </div>
                    </template>
                </div>
            </div>
        </section>
        <custom-suggest-widget
            v-if="enableActivePatient"
            id="patient-search"
            v-tooltip="{
                content: 'Sök patient',
                theme: 'kliniken-tooltip',
            }"
            placeholder="Sök patient"
            class="patient-searchBar"
            name="patientid"
            apisearch="/core/patienter/"
            @searchDone="searchDone"
            :open="searchOpen"
            :display-code="true"
            :map="
                (el) => {
                    return {
                        id: el.id,
                        code: el.personId_formatted,
                        displayName: el.fornamn + ' ' + el.efternamn,
                    };
                }
            "
        />

        <div
            v-if="showProfile"
            :class="{
                'button-wrapper': true,
            }"
            id="user-information"
        >
            <button
                class="user-details"
                type="button"
                :disabled="!fullName"
                role="button"
                v-tooltip="{
                    content: 'Inloggad användare',
                    theme: 'kliniken-tooltip',
                    disabled: Boolean(fullName) === false,
                }"
            >
                <UserInitials />
                <div class="user-name-info">
                    <span class="user-fullName">{{ fullName }}</span>
                    <img class="patient-arrow" src="../assets/dropdown-arrow-white.svg" />
                    <span class="profile-name">{{ currentProfile.roll.displayName }}</span>
                </div>
            </button>
            <div id="user-popup" class="information-details right" v-show="userDetailsOpen">
                <div class="details-row">
                    <UserInitials />
                    <div class="details-text">
                        <div class="small">Inloggad som</div>
                        <div class="big">{{ fullName }}</div>
                        <div class="medium">
                            {{ currentProfile.user.personId }}
                        </div>
                    </div>
                    <div class="profile-details">
                        <div class="medium"><b>Roll</b> {{ currentProfile.roll.displayName }}</div>
                        <div class="medium"><b>Yrke</b> {{ occupation }}</div>
                        <div class="medium"><b>Vårdenhet</b> {{ currentProfile.orgUnit.name }}</div>
                    </div>
                </div>
                <div class="user-buttons">
                    <Button
                        show-hover-effect
                        v-if="activeProfiles.length > 1"
                        class="hollow-button"
                        type="button"
                        role="button"
                        variant="hollow"
                        size="secondary"
                        @click="
                            changeProfile();
                            userDetailsOpen = false;
                        "
                    >
                        <img src="@/assets/user-circular-arrow.svg" />
                        <span>Byt användarprofil</span>
                    </Button>
                    <Button
                        show-hover-effect
                        class="hollow-button"
                        type="button"
                        role="button"
                        variant="hollow"
                        size="secondary"
                        @click="
                            editUser();
                            userDetailsOpen = false;
                        "
                    >
                        <img src="@/assets/anvandare.svg" />
                        <span>Min användarprofil</span>
                    </Button>
                    <div class="pt-spacer" />
                    <Button
                        show-hover-effect
                        class="hollow-button"
                        type="button"
                        role="button"
                        variant="hollow"
                        size="secondary"
                        v-on:click="
                            logOut();
                            userDetailsOpen = false;
                        "
                    >
                        <img src="@/assets/logout.svg" />
                        <span>Logga ut</span>
                    </Button>
                </div>
            </div>
        </div>
        <div v-else class="right logout">
            <Button show-hover-effect type="button" role="button" variant="hollow" size="secondary" v-on:click="logOut">
                <img src="@/assets/logga_ut_white.svg" />
                <span>Logga ut</span>
            </Button>
        </div>

        <svg xmlns:svg="<http://www.w3.org/2000/svg>" version="1.1" height="0" width="0" style="display: none">
            <defs>
                <filter id="gray">
                    <feColorMatrix
                        color-interpolation-filters="sRGB"
                        type="matrix"
                        values="0.52 0   0   0   0
                        0   0.61  0   0   0
                        0   0   0.64  0   0
                        0   0   0   1   0 "
                    />
                </filter>
            </defs>
            <defs>
                <filter id="soft-gray">
                    <feColorMatrix
                        color-interpolation-filters="sRGB"
                        type="matrix"
                        values="0.42 0   0   0   0
                        0   0.51  0   0   0
                        0   0   0.53  0   0
                        0   0   0   1   0 "
                    />
                </filter>
            </defs>
        </svg>
    </div>
</template>

<script>
    import LogoutIcon from "@/assets/logout.svg";
    import Button from "@/components/Button.vue";
    import UserInitials from "@/components/UserInitials.vue";
    import AlertInformationWidget from "@/components/widgets/AlertInformationWidget.vue";
    import HollowButton from "@/components/widgets/HollowButton.vue";
    import linkEvents from "@/linkEvents";
    import tabEvents, { setQueue } from "@/tabEvents";
    import AlertInformation from "@/tabs/AlertInformation/AlertInformation.vue";
    import CustomSuggestWidget from "@/tabs/Patient/CustomSuggestWidget.vue";
    import PatientRegistration from "@/tabs/Patient/PatientRegistration.vue";
    import PatientSearch from "@/tabs/Patient/PatientSearch.vue";
    import Samtycke from "@/tabs/Samtycke/Samtycke.vue";
    import Patientoversikt from "@/tabs/Patientoversikt/Patientoversikt.vue";
    import { flashMessage, flashWarning } from "@/utils";
    import { updatePurExternalPatientData } from "@/utils/patient/externalPatientData";
    import moment from "moment/moment";
    import emitter from "tiny-emitter/instance";
    import { mapActions, mapGetters } from "vuex";
    import { PersonIdTyp } from "@/utils/constants";
    import SystemInformation from "./SystemInformation.vue";
    import { ConfigModuleName, CoreConfigModuleName } from "@/store/modules/types/configModuleTypes";

    const TypeUserAction = Object.freeze({
        LOGOUT: "logout",
        CHANGE_PROFILE: "changeProfile",
    });

    export default {
        name: "KlinikenHeader",
        components: {
            UserInitials,
            Button,
            HollowButton,
            CustomSuggestWidget,
            AlertInformationWidget,
            SystemInformation,
        },
        props: {
            minimized: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                LogoutIcon,
                userDetailsOpen: false,
                uppmarksamhetOpen: false,
                patientDetailsOpen: false,
                alertInformationOpen: false,
                searchOpen: false,
                hidden: false, // Used for masking of patient data
                systemInfoOpen: false,
            };
        },
        computed: {
            ...mapGetters("patientData", [
                "patient",
                "personId",
                "personId_formatted",
                "fornamn",
                "efternamn",
                "namn",
                "gender",
                "dateOfBirth",
                "tilltalsnamn",
                "avliden",
                "sekretessmarkering",
                "skyddadIdentitet",
                "tilltalsnamnIndex",
                "fornamnslista",
                "fullstandigtNamn",
            ]),
            ...mapGetters("alertInformation", ["alertInformationSymbol", "alertInformationList"]),
            ...mapGetters("userData", [
                "fullName",
                "HSAId",
                "profiles",
                "currentProfile",
                "occupation",
                "activeProfiles",
            ]),
            ...mapGetters("loginMode", ["enableActivePatient", "showProfile"]),
            ...mapGetters("systemData", ["purConfigured"]),
            isTest() {
                return this.$store.state.systemData?.isTest
            },
            patientHasPersonIdTypePersonalNumber() {
                return this.patient.personIdType === PersonIdTyp.PERSONNUMMER;
            },
            patientFirstName() {
                if (!this.fornamnslista) {
                    return [];
                }

                return this.fornamnslista.map(function (fornamn, index) {
                    return {
                        fornamn: fornamn,
                        underline: this.tilltalsnamnIndex > -1 && index === this.tilltalsnamnIndex,
                    };
                }, this);
            },
        },
        methods: {
            updatePurExternalPatientData,
            ...mapActions("alertInformation", ["loadAlertInformationSymbol", "loadAlertInformationList"]),
            countYears() {
                if (this.dateOfBirth) {
                    const birth = moment(this.dateOfBirth);
                    const now = moment(Date.now());

                    return now.diff(birth, "years");
                }

                return null;
            },

            searchDone() {
                this.searchOpen = false;
            },
            async openSearchTab() {
                await this.$store.dispatch("tabs/closeTab", PatientSearch);
                await this.$store.dispatch("tabs/openTab", { component: PatientSearch });
            },
            async deselectPatient() {
                await this.$store.dispatch("patientData/resetPatientData");
            },
            openAlertTab() {
                this.alertInformationOpen = true;
            },
            closeAlertTab() {
                this.alertInformationOpen = false;
            },
            async openPatientTab() {
                await this.$store.dispatch("tabs/closeTabWithUnsavedDataCheck", PatientRegistration);
                await this.$store.dispatch("tabs/openTab", {
                    component: PatientRegistration,
                });
            },
            toggleSystemInfo() {
                this.systemInfoOpen = !this.systemInfoOpen;
                this.userDetailsOpen = false;
                this.patientDetailsOpen = false;
                this.uppmarksamhetOpen = false;
                this.searchOpen = false;
            },
            async openEditTab() {
                await this.$store.dispatch("tabs/closeTabWithUnsavedDataCheck", PatientRegistration);
                this.$store.commit("patientData/setEditPatient", this.patient.id);
                await this.$store.dispatch("tabs/openTab", {
                    component: PatientRegistration,
                });
            },
            openAlertInformationTab() {
                this.uppmarksamhetOpen = false;
                this.$store.dispatch("tabs/openTab", {
                    component: AlertInformation,
                });
            },
            openSamtyckeTab() {
                this.$store.dispatch("tabs/openTab", { component: Samtycke });
            },
            openPatientoversikt() {
                this.$store.dispatch("tabs/openTab", { component: Patientoversikt });
            },
            hidePatientData() {
                this.hidden = !this.hidden;
                this.$store.commit("patientData/hidePatientData", this.hidden);
            },
            /**
             * Checks if the user action is allowed and takes appropriate action if there is unsaved data.
             *
             * @param {string} typeUserAction - The type of user action. Must be either "logout" or "changeProfile".
             * @param {string} [dispatchActionPath=""] - The optional path to dispatch an action. Default is an empty string.
             */
            openWarningIfHasUnsavedData(typeUserAction, dispatchActionPath = "") {
                const ALLOWED_ACTIONS = new Map([
                    [
                        "logout",
                        {
                            description: "Du har osparad data. Vill du logga ut utan att spara?",
                            buttonLabel: "Ja, logga ut utan att spara",
                        },
                    ],
                    [
                        "changeProfile",
                        {
                            description: "Du har osparad data. Vill du byta profil utan att spara?",
                            buttonLabel: "Ja, byt profil utan att spara",
                        },
                    ],
                ]);

                if (!ALLOWED_ACTIONS.has(typeUserAction)) {
                    return;
                }

                const { description, buttonLabel } = ALLOWED_ACTIONS.get(typeUserAction);

                let hasUnsavedData;

                const tabs = this.$store.getters["tabs/tabs"] ?? [];

                tabs.forEach((tab) => {
                    if (tab.hasUnsavedData) {
                        hasUnsavedData = true;
                    }
                });

                if (hasUnsavedData) {
                    emitter.emit("open-dialog", {
                        type: "warning",
                        description,
                        buttons: [
                            {
                                title: "Avbryt",
                                type: "secondary",
                            },
                            {
                                title: buttonLabel,
                                type: "primary",
                                action: () => {
                                    this.$store.dispatch(dispatchActionPath);
                                },
                            },
                        ],
                    });
                } else {
                    this.$store.dispatch(dispatchActionPath);
                }
            },
            logOut() {
                this.openWarningIfHasUnsavedData(TypeUserAction.LOGOUT, "userData/logout");
            },
            editUser() {
                this.$store.dispatch("userAdmin/editSelf");
            },
            changeProfile() {
                this.openWarningIfHasUnsavedData(TypeUserAction.CHANGE_PROFILE, "userData/changeProfile");
            },
            queueOpenTab(component) {
                setQueue(component);
                this.patientDetailsOpen = true;
            },
            selectPatient(value) {
                if (value) {
                    value = JSON.parse(value);
                    if (value.id) {
                        this.searchOpen = false;

                        this.$store.dispatch("patientData/loadFromBackend", {
                            id: value.id,
                            failSilently: true,
                        });
                        linkEvents.$emit("update_patientid", {});

                        this.$store.dispatch("alertInformation/loadAlertInformationSymbol", { patientId: value.id });
                        this.$store.dispatch("alertInformation/loadAlertInformationList", { patientId: value.id });
                    }
                }
            },
            informationBarClick(event) {
                let patientDetailsClick = false;
                let userDetailsClick = false;
                let uppmarksamhetClick = false;
                let searchClick = false;

                let systemInfoClick = false;
                let targetElement = event.target;

                while (targetElement?.parentNode) {
                    const elementId = targetElement.id;

                    if (["kliniken-logo"].includes(elementId)) {
                        systemInfoClick = !this.systemInfoOpen;
                        break;
                    }

                    if (["systeminformation-popup"].includes(elementId)) {
                        systemInfoClick = this.systemInfoOpen;
                        break;
                    }

                    if (["patient-uppmarksamhet", "uppmarksamhet-popup"].includes(elementId)) {
                        if (elementId === "uppmarksamhet-popup") {
                            uppmarksamhetClick = this.uppmarksamhetOpen;

                            break;
                        }

                        uppmarksamhetClick = !this.uppmarksamhetOpen;

                        break;
                    }

                    if (["open-select-patient-popup", "open-patient-popup", "patient-popup"].includes(elementId)) {
                        if (elementId === "patient-popup") {
                            patientDetailsClick = this.patientDetailsOpen;

                            break;
                        }

                        patientDetailsClick = !this.patientDetailsOpen;

                        break;
                    }

                    if (["user-information", "user-popup"].includes(elementId)) {
                        if (elementId === "user-popup") {
                            userDetailsClick = this.userDetailsOpen;

                            break;
                        }

                        userDetailsClick = !this.userDetailsOpen;

                        break;
                    }

                    if (["patient-search", "patient-search-suggest-list"].includes(elementId)) {
                        if (elementId === "patient-search-suggest-list") {
                            searchClick = this.searchOpen;

                            break;
                        }

                        searchClick = true;

                        break;
                    }

                    if (!targetElement.parentNode) {
                        break;
                    }

                    targetElement = targetElement.parentNode;
                }

                this.systemInfoOpen = systemInfoClick;
                this.patientDetailsOpen = patientDetailsClick;
                this.userDetailsOpen = userDetailsClick;
                this.uppmarksamhetOpen = uppmarksamhetClick;
                this.searchOpen = searchClick;
            },
            async copyPersonIdToClipboard() {
                try {
                    let personId = this.personId;

                    try {
                        const copyPersonIdToClipboardFormat = this.$store.getters["config/configByName"]({
                            moduleName: ConfigModuleName.Core,
                            subModuleName: CoreConfigModuleName.CopyPersonIdToClipboardFormat,
                        });

                        // lägg till skiljetecken (- på position 8)
                        if (copyPersonIdToClipboardFormat?.[this.patient.personIdType]?.skiljetecken ?? false) {
                            personId = [personId.slice(0, 8), personId.slice(8)].join("-");
                        }

                        // ta bort sekel (första 2 tecknen)
                        if ((copyPersonIdToClipboardFormat?.[this.patient.personIdType]?.sekel ?? true) === false) {
                            personId = personId.substring(2);
                        }
                    } catch (error) {
                        console.error(error);
                    }
                    await navigator.clipboard.writeText(personId);
                    flashMessage("PersonID kopierat");
                } catch (error) {
                    console.error(error.message);
                    flashWarning("Kunde inte kopiera personID till clipboard");
                }
            },
            async copyPatientIdToClipboard() {
                try {
                    await navigator.clipboard.writeText(this.patient.patientId);
                    flashMessage("PatientID kopierat");
                } catch (error) {
                    console.error(error.message);
                    flashWarning("Kunde inte kopiera patientID till clipboard");
                }
            },
        },

        beforeCreate() {
            if (process.env.NODE_ENV === "development") {
                let patientId = sessionStorage.getItem("PatientId");
                if (patientId) {
                    this.$store.dispatch("patientData/loadFromBackend", { id: patientId, failSilently: true });
                    this.$store.dispatch("alertInformation/loadAlertInformationSymbol", { patientId: patientId });
                    this.$store.dispatch("alertInformation/loadAlertInformationList", { patientId: patientId });
                }
            }
        },
        created() {
            document.addEventListener("click", this.informationBarClick);
            tabEvents.$on("patientSelectRequired", this.queueOpenTab);
            tabEvents.$on("closingAlertInformationTab", this.closeAlertTab);
            tabEvents.$on("openingAlertInformationTab", this.openAlertTab);
            linkEvents.$on("patientid_changed", this.selectPatient);
        },
        beforeDestroy() {
            document.removeEventListener("click", this.informationBarClick);
            tabEvents.$off("patientSelectRequired", this.queueOpenTab);
            tabEvents.$off("closingAlertInformationTab", this.closeAlertTab);
            tabEvents.$off("openingAlertInformationTab", this.openAlertTab);
            linkEvents.$off("patientid_changed", this.selectPatient);
        },
    };
</script>

<style lang="scss" scoped>
    @import "@/style/variables";

    #kliniken-header {
        width: 100%;
        background-color: $main-header-bg;
        height: $header-height;
        text-align: center;
        display: grid;
        grid-auto-flow: column;
        align-items: center;
        grid-gap: 3rem;
        padding-right: 3rem;

        .systeminformation-popup {
            margin-left: 1rem;
        }

        &.test-bg {
            background-color: $main-header-test-bg;
        }

        .logout {
            justify-self: flex-end;

            button {
                color: $color-button-text;
            }
        }

        &.patient {
            grid-template-columns: max-content max-content auto max-content;
        }

        &.disable-patient {
            grid-template-columns: max-content auto;
        }

        #kliniken-logo {
            display: grid;
            place-items: center;
            height: 100%;
            width: $menu-maximized-width;
            background-color: $main-logo-bg;
            text-align: center;
            position: relative;

            &.minimized {
                max-width: $menu-minimized-width;
            }

            &.test-bg {
                background-color: $main-header-test-bg-dark;
                color: white;
                text-align: center;
                font-weight: bold;
            }
        }

        @media screen and (max-width: 1250px) {
            grid-gap: 1rem;
            padding-right: 1rem;
        }

        .suggest-wrap {
            justify-self: flex-end;

            @media screen and (max-width: 900px) {
                display: none;
            }
        }

        .patient-section {
            display: grid;
            height: 100%;

            #patient-information {
                display: grid;
                align-items: center;
                grid-auto-flow: column;
                width: fit-content;
                padding: 0.75rem 1rem;
                font-size: 0.9rem;

                &:has(.patient-details.patient-selected) {
                    border-top-left-radius: $border-radius-pop-up;
                    border-top-right-radius: $border-radius-pop-up;
                    border-bottom-right-radius: 0;
                    border-bottom-left-radius: 0;
                    background-color: $color-grey-bg;
                    align-self: flex-end;
                    border-bottom: 1px solid $divider-color;
                }

                #patient-popup {
                    &.has-person-id {
                        left: 4rem;
                    }

                    &:not(.has-person-id) {
                        .details-buttons {
                            margin-top: 2rem;
                        }
                    }
                }

                .patient-details {
                    background-color: inherit;
                    border: none;
                    outline: none;
                    display: grid;
                    grid-auto-flow: column;
                    grid-gap: 0.5rem;
                    align-items: center;
                    color: $color-button-text;

                    &.patient-selected {
                        color: $color-table-header;
                        grid-template-columns: max-content auto auto;
                        grid-template-rows: 1fr 1fr;
                        grid-gap: 0.25rem;

                        .patient-name,
                        .id-container {
                            grid-column: 1 / 3;
                            justify-self: flex-start;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            width: 100%;
                            text-wrap: nowrap;
                            text-align: start;
                        }

                        .person-id {
                            font-weight: bold;
                        }

                        .person-age {
                            font-size: 0.7rem;
                            padding: 0 1rem;
                        }

                        .patient-arrow {
                            height: 0.8rem;
                            grid-column: 3;
                            grid-row: 2;
                            justify-self: flex-end;
                        }
                    }
                }
            }

            #patient-uppmarksamhet {
                border-right: 1px solid $divider-color;
                padding-right: 0.8rem;
                margin-right: 0.8rem;
                display: grid;
                align-items: center;
                height: 100%;

                .open-alertinformation {
                    width: 100%;
                }
            }
        }

        #user-information {
            display: grid;
            align-items: center;
            height: 100%;
            justify-self: flex-end;
            margin: 0;

            > button {
                padding: 0;
                height: 100%;

                &:first-child {
                    .user-fullName,
                    .profile-name {
                        @media screen and (max-width: 1250px) {
                            max-width: min-content;
                        }

                        @media screen and (max-width: 1170px) {
                            display: none;
                        }
                    }
                }
            }

            #user-popup {
                .user-icon {
                    align-self: flex-end;
                }

                .user-buttons {
                    display: grid;

                    .hollow-button {
                        display: grid;
                        grid-gap: 0.2rem;
                        justify-content: flex-start;
                        align-content: center;
                        grid-auto-flow: column;
                        color: $color-text-dark;
                        height: 3rem;

                        &:hover {
                            background-color: $color-hover;
                        }
                    }
                }
            }

            .user-details {
                display: grid;
                grid-auto-flow: column;
                align-items: center;
                grid-gap: 0.8rem;
                background-color: inherit;
                border: none;
                outline: none;
                color: $color-button-text;
                font-size: 0.9rem;

                .user-name-info {
                    display: grid;
                    align-items: center;
                    grid-column-gap: 0.6rem;
                    grid-row-gap: 0.4rem;
                    justify-items: flex-start;

                    .user-fullName,
                    .patient-arrow {
                        grid-row: 1;
                    }

                    .patient-arrow {
                        height: 0.8rem;
                    }

                    .profile-name {
                        font-size: 0.8rem;
                        grid-column: 1 / -1;
                        justify-self: flex-start;
                    }
                }
            }
        }

        .button-wrapper-alert,
        .button-wrapper {
            position: relative;
            width: auto;
            font-family: Roboto;
            font-size: 17px;
            line-height: 26px;

            &.active {
                background: linear-gradient(to top, #ca346f 0%, #ca346f 100%);
                background-repeat: no-repeat;
                background-size: 100% 5px;
                background-position: center bottom;
            }

            button.patient-details {
                padding: 0;
            }
        }

        .information-details {
            -webkit-box-sizing: border-box;
            -moz-box-sizing: border-box;
            box-sizing: border-box;
            text-align: left;
            min-width: 22rem;
            width: max-content;
            max-width: 28rem;
            box-shadow: $dropdown-box-shadow;
            border-radius: $border-radius-pop-up;
            position: absolute;
            top: calc(100% + 0.2rem);
            z-index: 200;
            background: #ffffff;
            color: #000000;
            padding: 1.5rem;

            &.right {
                right: 0;
            }

            &.left {
                left: 0;
            }

            .details-buttons {
                display: grid;
                justify-items: flex-start;
                grid-gap: 0.8rem;
                align-items: center;
            }

            .warning {
                color: #354052;
                font-size: 16px;
                line-height: 24px;
                font-weight: 400;
                display: inline-block;
                padding-left: 30px;
                position: relative;

                &::before {
                    content: "!";
                    padding-left: 6.25px;
                    padding-top: 2.5px;
                    color: #fff;
                    font-size: 12px;
                    line-height: 12px;
                    font-weight: bold;
                    height: 16px;
                    width: 16px;
                    border-radius: 8px;
                    border: none;
                    background-color: #354052;
                    position: absolute;
                    left: 0px;
                    top: 3px;
                }
            }

            .details-row {
                display: grid;
                grid-auto-flow: column;
                grid-template-columns: auto 1fr;
                align-items: center;
                grid-column-gap: 0.8rem;
                padding-bottom: 1rem;

                .patient-icon {
                    display: grid;
                    place-items: center;
                    height: 50px;
                    width: 50px;
                    border-radius: 25px;
                    background-color: #92c9dd;

                    img {
                        padding: 12px;
                    }
                }

                .user-icon {
                    height: 50px;
                    width: 50px;
                    border-radius: 25px;
                    background-color: $color-table-header;
                    text-align: center;
                    color: $color-button-text;

                    span {
                        font-size: 20px;
                        letter-spacing: 0.63px;
                        line-height: 50px;
                        padding-left: 0px;
                    }
                }

                .profile-details {
                    grid-column: 2;
                    grid-row: 2;
                    margin-top: 12px;
                    font-size: 0.9rem;
                }

                .details-text {
                    grid-row: 1;
                    flex: 1 1 auto;

                    .small {
                        color: #354052;
                        font-size: 14px;
                        line-height: 22px;
                    }

                    .medium {
                        color: #354052;
                        font-family: Roboto;
                        font-size: 16px;
                        line-height: 24px;
                    }

                    .big {
                        color: #55676d;
                        font-family: Roboto;
                        font-size: 20px;
                        line-height: 28px;
                    }
                }
            }

            .pt-spacer {
                margin: 0.4rem 0;
                background-color: #95a1b1;
                height: 1px;
                width: 100%;
            }

            .gray {
                filter: url("#gray");
            }

            .soft-gray {
                filter: url("#soft-gray");
            }

            .blue-button,
            .hollow-button {
                margin: 0;
                display: grid;
                grid-auto-flow: column;
                align-items: center;
                grid-gap: 0.8rem;
                height: 28px;
                color: #6c8188;
                font-family: Roboto Medium;
                font-size: 16px;
                font-weight: 500;
                line-height: 28px;
                border: none;
                background-color: #ffffff;
            }

            .hollow-button {
                border-radius: 0;
            }

            .hollow-button {
                border-radius: 0;
            }

            .blue-button {
                width: 100%;
                border-radius: 4px;
                grid-auto-columns: max-content;
                padding-left: 1rem;
                height: 46px;
                background-color: #38a7cf;
                color: #ffffff;
            }
        }

        .alertSymbol {
            align-self: center;
            cursor: pointer;
        }
    }

    .spacer {
        margin-top: 33px;
        margin-bottom: 25px;
    }

    .uppmarksamhet-details {
        .details-row {
            display: flex;

            .details-text {
                flex: 1 1 auto;
                color: #55676d;

                .small {
                    font-size: 14px;
                    line-height: 22px;
                }

                .medium {
                    font-family: Roboto;
                    font-size: 16px;
                    line-height: 24px;
                }

                .big {
                    font-family: Roboto;
                    font-size: 20px;
                    line-height: 28px;
                }
            }
        }
    }

    .patient-section:hover {
        .close-x {
            visibility: visible;
        }
    }

    .close-x {
        font-size: 15px;
        font-weight: 100;
        position: relative;
        visibility: hidden;
    }

    .close-x,
    #open-patient-popup {
        margin-left: 1rem;
    }

    .id-clip {
        padding-left: 5px;
        visibility: hidden;
    }

    .id-container:hover {
        .id-clip {
            visibility: visible;
        }
    }

    .bold {
        font-weight: bold;
    }
</style>
